import React, { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Eye,
  Search,
  X,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Progress } from "~/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { FacturaCost } from "~/services/facturaCostsService";
import { SalesRequestDocument } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";
import { resolveGross } from "~/utils/iva";

dayjs.extend(utc);

// Function to get color classes based on sales condition name
const getSalesConditionColor = (conditionName: string) => {
  if (
    !conditionName ||
    conditionName === "N/A" ||
    conditionName === "Sin condición"
  ) {
    return "bg-gray-100 text-gray-800";
  }

  // ANTICIPADO - Prepaid/advance payment (green)
  if (conditionName === "ANTICIPADO") {
    return "bg-green-100 text-green-800";
  }

  // CON. ENTREGA - Payment on delivery (purple)
  if (conditionName.includes("CON. ENTREGA")) {
    return "bg-purple-100 text-purple-800";
  }

  // CREDITO - Credit line (orange)
  if (conditionName.startsWith("CREDITO")) {
    return "bg-orange-100 text-orange-800";
  }

  // TELECHEQUE - External platform (indigo)
  if (conditionName.startsWith("TELECHEQUE")) {
    return "bg-indigo-100 text-indigo-800";
  }

  // TRANSFERENCIA - Internal credit line (amber)
  if (conditionName.startsWith("TRANSFERENCIA")) {
    return "bg-amber-100 text-amber-800";
  }

  // MERCADOPAGO or other external platforms (indigo)
  if (conditionName.includes("MERCADOPAGO") || conditionName.includes("PAGO")) {
    return "bg-indigo-100 text-indigo-800";
  }

  // Default blue for unknown conditions
  return "bg-blue-100 text-blue-800";
};

interface FacturaWithPayments extends SalesRequestDocument {
  totalPaid?: number;
  paymentProgress?: number;
}

interface FacturasTableProps {
  facturas: FacturaWithPayments[];
  loading: boolean;
  clientNames: Record<string, string>;
  clientTaxIds: Record<string, string>;
  onFacturaClick: (factura: FacturaWithPayments) => void;
  /** WAC cost per sales_request (keyed by sales_request_id), for cost/margin columns. */
  costMap?: Map<string, FacturaCost>;
  /** Current user role — cost/margin columns are hidden from sellers. */
  userRole?: string;
}

type SortField =
  | "numero"
  | "cliente"
  | "fechaEmision"
  | "condicion"
  | "fechaVencimiento"
  | "monto"
  | "progreso";
type SortDirection = "asc" | "desc";

interface SortConfig {
  field: SortField | null;
  direction: SortDirection;
}

const FacturasTable: React.FC<FacturasTableProps> = ({
  facturas,
  loading,
  clientNames,
  clientTaxIds,
  onFacturaClick,
  costMap,
  userRole,
}) => {
  // Cost & margin are sensitive — sellers never see these columns.
  const showCostMargin = userRole !== "seller";
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    direction: "asc",
  });

  // Handle sort changes
  const handleSort = (field: SortField) => {
    setSortConfig((prevConfig) => ({
      field,
      direction:
        prevConfig.field === field && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Filter facturas based on search query
  const filteredFacturas = useMemo(() => {
    if (!searchQuery.trim()) return facturas;

    const query = searchQuery.toLowerCase();
    return facturas.filter((factura) => {
      // Search by factura number
      const bsaleNumber = (factura as any).bsale_number
        ?.toString()
        .toLowerCase();
      if (bsaleNumber?.includes(query)) return true;

      // Search by client name
      const clientId = factura.clientId;
      const clientName = clientId ? clientNames[clientId]?.toLowerCase() : "";
      if (clientName?.includes(query)) return true;

      // Search by client tax ID
      const clientTaxId = clientId ? clientTaxIds[clientId]?.toLowerCase() : "";
      if (clientTaxId?.includes(query)) return true;

      // Search by sales condition
      const salesCondition = (
        factura as any
      ).sales_condition_name?.toLowerCase();
      if (salesCondition?.includes(query)) return true;

      // Search by amount (use net_amount from documents, fallback to total_amount)
      const totalAmount = (
        (factura as any).net_amount || (factura as any).total_amount
      )?.toString();
      if (totalAmount?.includes(query.replace(/[,$]/g, ""))) return true;

      return false;
    });
  }, [facturas, searchQuery, clientNames, clientTaxIds]);

  // Sort facturas based on current sort config
  const sortedFacturas = useMemo(() => {
    if (!sortConfig.field) return filteredFacturas;

    return [...filteredFacturas].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case "numero":
          aValue = (a as any).bsale_number || "";
          bValue = (b as any).bsale_number || "";
          break;
        case "cliente":
          aValue = a.clientId ? clientNames[a.clientId] || "" : "";
          bValue = b.clientId ? clientNames[b.clientId] || "" : "";
          break;
        case "fechaEmision":
          // Use emission_date if available, otherwise fall back to other dates
          aValue = (a as any).emission_date
            ? (a as any).emission_date
            : (a as any).factura_creation_date
              ? new Date((a as any).factura_creation_date).getTime()
              : a.createdAt
                ? new Date(a.createdAt).getTime()
                : 0;
          bValue = (b as any).emission_date
            ? (b as any).emission_date
            : (b as any).factura_creation_date
              ? new Date((b as any).factura_creation_date).getTime()
              : b.createdAt
                ? new Date(b.createdAt).getTime()
                : 0;
          break;
        case "condicion":
          aValue = (a as any).sales_condition_name || "";
          bValue = (b as any).sales_condition_name || "";
          break;
        case "fechaVencimiento":
          aValue = (a as any).payment_due_date
            ? new Date((a as any).payment_due_date).getTime()
            : 0;
          bValue = (b as any).payment_due_date
            ? new Date((b as any).payment_due_date).getTime()
            : 0;
          break;
        case "monto":
          // Use net_amount from documents, fallback to total_amount
          aValue = Number(
            (a as any).net_amount || (a as any).total_amount || 0
          );
          bValue = Number(
            (b as any).net_amount || (b as any).total_amount || 0
          );
          break;
        case "progreso":
          aValue = a.paymentProgress || 0;
          bValue = b.paymentProgress || 0;
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric comparison
      if (sortConfig.direction === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [filteredFacturas, sortConfig, clientNames]);

  // Calculate pagination using sorted facturas
  const { paginatedFacturas, totalPages, startIndex, endIndex } =
    useMemo(() => {
      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const paginated = sortedFacturas.slice(start, end);
      const pages = Math.ceil(sortedFacturas.length / itemsPerPage);

      return {
        paginatedFacturas: paginated,
        totalPages: pages,
        startIndex: start + 1,
        endIndex: Math.min(end, sortedFacturas.length),
      };
    }, [sortedFacturas, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to page 1 when facturas data changes (e.g., due to date filter changes) or search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [facturas.length, searchQuery]);

  // Sortable header component
  const SortableHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
  }> = ({ field, children }) => {
    const isActive = sortConfig.field === field;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <TableHead
        className="cursor-pointer select-none hover:bg-gray-50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col">
            <ChevronUp
              className={`h-3 w-3 ${isActive && direction === "asc" ? "text-blue-600" : "text-gray-400"}`}
            />
            <ChevronDown
              className={`-mt-1 h-3 w-3 ${isActive && direction === "desc" ? "text-blue-600" : "text-gray-400"}`}
            />
          </div>
        </div>
      </TableHead>
    );
  };
  if (loading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Factura</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Fecha Emisión</TableHead>
                  <TableHead>Condición</TableHead>
                  <TableHead>Fecha Vencimiento</TableHead>
                  <TableHead>Monto Total (sin IVA)</TableHead>
                  {showCostMargin && (
                    <>
                      <TableHead>Costo Total</TableHead>
                      <TableHead>Margen</TableHead>
                      <TableHead>% Margen</TableHead>
                    </>
                  )}
                  <TableHead>Progreso de Pago</TableHead>
                  <TableHead>Nota de Venta</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-28 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    {showCostMargin && (
                      <>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <div className="w-full max-w-[120px]">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-2 flex-1" />
                          <Skeleton className="h-4 w-8" />
                        </div>
                        <Skeleton className="mt-1 h-3 w-full" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (sortedFacturas.length === 0) {
    const message = searchQuery
      ? `No se encontraron facturas que coincidan con "${searchQuery}".`
      : "No se encontraron facturas.";
    return (
      <Card>
        <CardContent className="p-0">
          {/* Search Bar */}
          <div className="border-b border-gray-200 bg-gray-50 p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por número, cliente, RUT o condición..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="p-8 text-center">{message}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Search Bar */}
        <div className="border-b border-gray-200 bg-gray-50 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar por número, cliente, RUT o condición..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader field="numero">Nº Factura</SortableHeader>
                <SortableHeader field="cliente">Cliente</SortableHeader>
                <SortableHeader field="fechaEmision">
                  Fecha Emisión
                </SortableHeader>
                <SortableHeader field="condicion">Condición</SortableHeader>
                <SortableHeader field="fechaVencimiento">
                  Fecha Vencimiento
                </SortableHeader>
                <SortableHeader field="monto">
                  Monto Total (sin IVA)
                </SortableHeader>
                {showCostMargin && (
                  <>
                    <TableHead className="text-right">Costo Total</TableHead>
                    <TableHead className="text-right">Margen</TableHead>
                    <TableHead className="text-right">% Margen</TableHead>
                  </>
                )}
                <SortableHeader field="progreso">
                  Progreso de Pago
                </SortableHeader>
                <TableHead>Nota de Venta</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedFacturas.map((factura, index) => {
                const clientName = factura.clientId
                  ? clientNames[factura.clientId] || "Cliente no encontrado"
                  : "Cliente no encontrado";

                const clientTaxId = factura.clientId
                  ? clientTaxIds[factura.clientId] || ""
                  : "";

                // Use bsale_number from database instead of extracting from filename
                const bsaleNumber =
                  (factura as any).bsale_number || "Sin número";

                // Check if this is a test factura (from test sales request)
                const isTestFactura =
                  (factura as any).sales_request_status === "test";

                // Check if this factura is cancelled
                const isCancelled = (factura as any).cancelled === true;

                // Get additional data from the enhanced factura
                const salesConditionName =
                  (factura as any).sales_condition_name || "Sin condición";
                const paymentDueDate = (factura as any).payment_due_date;
                const totalPaid = factura.totalPaid || 0;
                const paymentProgress = factura.paymentProgress || 0;
                const priceWithIva = resolveGross(factura as any);
                const isPaid = (factura as any).sales_request_paid || false;

                // Check if payment is overdue
                const isOverdue =
                  !isPaid &&
                  paymentDueDate &&
                  new Date() > new Date(paymentDueDate);

                // Check if payment is due soon (within 5 days)
                const isDueSoon =
                  !isPaid &&
                  paymentDueDate &&
                  (() => {
                    const today = new Date();
                    const dueDate = new Date(paymentDueDate);
                    const diffTime = dueDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    return diffDays > 0 && diffDays <= 5;
                  })();

                // Cost & margin (WAC) — only meaningful for facturas; NC/ND
                // show "—". "sin costo" when the period wasn't costed yet.
                const isFactura = factura.documentType === "factura";
                const netAmount = Number(
                  (factura as any).net_amount ||
                    (factura as any).total_amount ||
                    0
                );
                const costEntry =
                  isFactura && factura.salesRequestId
                    ? costMap?.get(factura.salesRequestId)
                    : undefined;
                const costTotal = costEntry?.costTotal ?? 0;
                const hasCost =
                  (costEntry?.costedLineCount ?? 0) > 0 && costTotal > 0;
                const margen = hasCost ? netAmount - costTotal : null;
                const margenPct =
                  margen !== null && netAmount !== 0
                    ? (margen / netAmount) * 100
                    : null;

                return (
                  <TableRow
                    key={factura.id}
                    style={{
                      animationDelay: `${Math.min(index * 40, 400)}ms`,
                    }}
                    className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${
                      isTestFactura
                        ? "text-gray-500 opacity-50"
                        : isCancelled
                          ? "bg-gray-50 hover:bg-gray-100"
                          : isPaid
                            ? "bg-green-50 hover:bg-green-100"
                            : isOverdue
                              ? "bg-red-50 hover:bg-red-100"
                              : isDueSoon
                                ? "bg-yellow-50 hover:bg-yellow-100"
                                : ""
                    }`}
                  >
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      <div className="flex items-center gap-2">
                        <span>{bsaleNumber}</span>
                        {isTestFactura && (
                          <span className="ml-1 text-xs">(TEST)</span>
                        )}
                        {isCancelled && (
                          <Badge variant="destructive" className="text-xs">
                            Anulado
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      <div>
                        <div>{clientName}</div>
                        {clientTaxId && (
                          <div className="mt-1 text-xs text-gray-500">
                            {clientTaxId}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      {/* Use emission_date if available, otherwise fall back to factura_creation_date, then createdAt */}
                      {(factura as any).emission_date
                        ? (() => {
                            // emission_date is stored as bigint (Unix timestamp in seconds)
                            const timestamp = (factura as any).emission_date;
                            const date = dayjs
                              .unix(timestamp)
                              .utc()
                              .locale("es"); // Parse Unix timestamp in UTC to avoid timezone issues
                            return date.isValid()
                              ? date.format("DD MMM YYYY")
                              : "Fecha inválida";
                          })()
                        : (factura as any).factura_creation_date
                          ? (() => {
                              const date = dayjs(
                                (factura as any).factura_creation_date
                              ).locale("es");
                              return date.isValid()
                                ? date.format("DD MMM YYYY")
                                : "Fecha inválida";
                            })()
                          : factura.createdAt
                            ? (() => {
                                const date = dayjs(factura.createdAt).locale(
                                  "es"
                                );
                                return date.isValid()
                                  ? date.format("DD MMM YYYY")
                                  : "Fecha inválida";
                              })()
                            : "N/A"}
                    </TableCell>
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getSalesConditionColor(salesConditionName)}`}
                      >
                        {salesConditionName}
                      </span>
                    </TableCell>
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      {paymentDueDate
                        ? (() => {
                            const date = dayjs(paymentDueDate).locale("es");
                            return date.isValid()
                              ? date.format("DD MMM YYYY")
                              : "Fecha inválida";
                          })()
                        : "Sin fecha"}
                    </TableCell>
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      {/* Display net_amount from sales_request_documents (BSale values), fallback to total_amount */}
                      $
                      {formatCurrency(
                        Math.round(
                          (factura as any).net_amount ||
                            (factura as any).total_amount ||
                            0
                        )
                      )}
                      <span className="ml-1 text-xs text-gray-500">
                        (sin IVA)
                      </span>
                    </TableCell>
                    {showCostMargin && (
                      <>
                        <TableCell
                          className={`text-right ${isTestFactura ? "text-gray-400" : ""}`}
                        >
                          {hasCost ? (
                            `$${formatCurrency(Math.round(costTotal))}`
                          ) : isFactura ? (
                            <span className="text-amber-600">sin costo</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-right ${
                            isTestFactura
                              ? "text-gray-400"
                              : margen !== null && margen < 0
                                ? "font-medium text-red-600"
                                : "font-medium"
                          }`}
                        >
                          {margen !== null ? (
                            `$${formatCurrency(Math.round(margen))}`
                          ) : (
                            <span className="font-normal text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-right ${
                            isTestFactura
                              ? "text-gray-400"
                              : margenPct !== null && margenPct < 0
                                ? "text-red-600"
                                : ""
                          }`}
                        >
                          {margenPct !== null ? (
                            `${margenPct.toFixed(1)}%`
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell className={isTestFactura ? "text-gray-400" : ""}>
                      <div className="w-full max-w-[120px]">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={paymentProgress}
                            className="h-2 flex-1"
                          />
                          <span className="min-w-[35px] text-xs font-medium">
                            {Math.round(paymentProgress)}%
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          ${formatCurrency(totalPaid)} / $
                          {formatCurrency(priceWithIva)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {factura.salesRequestId ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onFacturaClick(factura)}
                          className={`flex items-center gap-1 ${
                            isTestFactura
                              ? "border-gray-300 text-gray-400 opacity-50"
                              : ""
                          }`}
                          title="Ver detalles de la nota de venta"
                        >
                          <Eye className="h-4 w-4" />
                          Abrir Factura
                          {isTestFactura && (
                            <span className="text-xs">(TEST)</span>
                          )}
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-500">
                          Sin nota de venta
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => window.open(factura.fileUrl, "_blank")}
                          title="Descargar factura"
                          className={
                            isTestFactura
                              ? "border-gray-300 text-gray-400 opacity-50"
                              : ""
                          }
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {sortedFacturas.length > 0 && (
          <div className="flex items-center justify-between border-t px-6 py-4">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>
                Mostrando {startIndex} a {endIndex} de {sortedFacturas.length}{" "}
                facturas
              </span>
              <div className="flex items-center gap-2">
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={handleItemsPerPageChange}
                >
                  <SelectTrigger className="h-8 w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Show first page, last page, current page and 2 pages around current
                      const current = currentPage;
                      return (
                        page === 1 ||
                        page === totalPages ||
                        (page >= current - 2 && page <= current + 2)
                      );
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;

                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && (
                            <span className="px-2 text-muted-foreground">
                              ...
                            </span>
                          )}
                          <Button
                            variant={
                              currentPage === page ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FacturasTable;
